import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { ArrowLeft, Users, Share2, Award, Wallet, Copy, Check, AlertCircle, Settings, Trash2, MessageSquare, Receipt, PieChart, Zap, Image, Shield, Menu, MoreHorizontal } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const GroupDetails = ({ user }) => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [contributionAmount, setContributionAmount] = useState('');
  const [upiTransactionId, setUpiTransactionId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showCustomAmountDialog, setShowCustomAmountDialog] = useState(false);
  const [customPayAmount, setCustomPayAmount] = useState('');
  const [paymentDisclaimerAccepted, setPaymentDisclaimerAccepted] = useState(false);
  const [showPaymentDisclaimer, setShowPaymentDisclaimer] = useState(false);
  useEffect(() => {
    fetchGroupDetails();
  }, [groupId]);

  useEffect(() => {
    if (stats?.group?.creator_id === user.id) {
      fetchPendingCount();
    }
  }, [stats, user.id]);

  const fetchGroupDetails = async () => {
    try {
      const [statsResponse, contributionsResponse] = await Promise.all([
        axios.get(`${API}/groups/${groupId}/stats`),
        axios.get(`${API}/contributions/group/${groupId}`)
      ]);
      setStats(statsResponse.data);
      setContributions(contributionsResponse.data);
    } catch (error) {
      toast.error('Failed to fetch group details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingCount = async () => {
    try {
      const response = await axios.get(`${API}/contributions/pending/${groupId}?user_id=${user.id}`);
      setPendingCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch pending count');
    }
  };

  const handleStartPayment = () => {
    if (!stats.group.upi_id) {
      toast.error('Payment account not configured. Please contact group admin.');
      return;
    }
    
    const amount = stats.group.daily_contribution;
    if (!amount || amount <= 0) {
      toast.error('Invalid contribution amount. Please contact group admin.');
      return;
    }
    
    // Clean amount - remove decimals if whole number
    const cleanAmount = Number.isInteger(amount) ? amount : amount.toFixed(2);
    const upiId = stats.group.upi_id.trim();
    const name = (stats.group.bank_holder_name || 'SharePay').replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 20);
    const note = `${stats.group.name} Contribution`.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 50);
    
    // Create UPI intent URL - standard format
    const upiLink = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(name)}&am=${cleanAmount}&cu=INR&tn=${encodeURIComponent(note)}`;
    
    console.log('UPI Link:', upiLink);
    
    // Direct navigation - most reliable method
    window.location.href = upiLink;
    
    // Show payment dialog after a delay to record transaction
    setTimeout(() => {
      setShowPaymentDialog(true);
      setContributionAmount(cleanAmount.toString());
    }, 1500);
  };

  const handlePayWithApp = (app) => {
    if (!stats.group.upi_id) {
      toast.error('Payment account not configured.');
      return;
    }
    
    const amount = stats.group.daily_contribution;
    if (!amount || amount <= 0) {
      toast.error('Invalid contribution amount. Please set up daily contribution amount.');
      return;
    }
    
    // Clean amount and parameters
    const cleanAmount = Number.isInteger(amount) ? amount : amount.toFixed(2);
    const upiId = stats.group.upi_id.trim();
    const name = (stats.group.bank_holder_name || 'SharePay').replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 20);
    const note = `${stats.group.name} Contribution`.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 50);
    
    // Encode parameters
    const encodedUpi = encodeURIComponent(upiId);
    const encodedName = encodeURIComponent(name);
    const encodedNote = encodeURIComponent(note);
    
    let appLink = '';
    
    switch(app) {
      case 'gpay':
        // Google Pay - use tez:// or upi://
        appLink = `tez://upi/pay?pa=${encodedUpi}&pn=${encodedName}&am=${cleanAmount}&cu=INR&tn=${encodedNote}`;
        break;
      case 'phonepe':
        // PhonePe
        appLink = `phonepe://pay?pa=${encodedUpi}&pn=${encodedName}&am=${cleanAmount}&cu=INR&tn=${encodedNote}`;
        break;
      case 'paytm':
        // Paytm
        appLink = `paytmmp://pay?pa=${encodedUpi}&pn=${encodedName}&am=${cleanAmount}&cu=INR&tn=${encodedNote}`;
        break;
      default:
        // Generic UPI
        appLink = `upi://pay?pa=${encodedUpi}&pn=${encodedName}&am=${cleanAmount}&cu=INR&tn=${encodedNote}`;
    }
    
    console.log('App Link:', appLink);
    
    // Direct navigation
    window.location.href = appLink;
    
    // Create an anchor and click it
    const link = document.createElement('a');
    link.href = appLink;
    link.click();
    
    // Show payment dialog after a delay
    setTimeout(() => {
      setShowPaymentDialog(true);
      setContributionAmount(amount.toString());
    }, 1000);
  };

  const handleCopyUpi = () => {
    navigator.clipboard.writeText(stats.group.upi_id);
    setCopied(true);
    toast.success('UPI ID copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePayCustomAmount = () => {
    const amount = parseFloat(customPayAmount);
    if (!amount || amount < 1) {
      toast.error('Please enter a valid amount (minimum ₹1)');
      return;
    }
    
    if (!stats.group.upi_id) {
      toast.error('Payment account not configured.');
      return;
    }
    
    // Clean parameters
    const cleanAmount = Number.isInteger(amount) ? amount : amount.toFixed(2);
    const upiId = stats.group.upi_id.trim();
    const name = (stats.group.bank_holder_name || 'SharePay').replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 20);
    const note = `${stats.group.name} Contribution`.replace(/[^a-zA-Z0-9 ]/g, '').substring(0, 50);
    
    const upiLink = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(name)}&am=${cleanAmount}&cu=INR&tn=${encodeURIComponent(note)}`;
    
    console.log('Custom UPI Link:', upiLink);
    
    window.location.href = upiLink;
    
    setShowCustomAmountDialog(false);
    setTimeout(() => {
      setShowPaymentDialog(true);
      setContributionAmount(cleanAmount.toString());
    }, 1500);
  };

  // Payment disclaimer before initiating any payment
  const initiatePaymentWithDisclaimer = (paymentFunction, ...args) => {
    if (paymentDisclaimerAccepted) {
      paymentFunction(...args);
    } else {
      setShowPaymentDisclaimer(true);
      window._pendingPayment = () => paymentFunction(...args);
    }
  };

  const acceptDisclaimerAndPay = () => {
    setPaymentDisclaimerAccepted(true);
    setShowPaymentDisclaimer(false);
    if (window._pendingPayment) {
      window._pendingPayment();
      delete window._pendingPayment;
    }
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!contributionAmount || parseFloat(contributionAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!upiTransactionId.trim()) {
      toast.error('Please enter the UPI transaction ID');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contributions?user_id=${user.id}`, {
        group_id: groupId,
        amount: parseFloat(contributionAmount),
        upi_transaction_id: upiTransactionId.trim()
      });
      toast.success('Payment submitted for verification!');
      setShowPaymentDialog(false);
      setContributionAmount('');
      setUpiTransactionId('');
      fetchGroupDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteGroup = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/groups/${groupId}?user_id=${user.id}`);
      toast.success('Group deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete group');
      setDeleting(false);
    }
  };

  const getLevelBadge = (level) => {
    const levels = {
      1: 'level-1', 2: 'level-2', 3: 'level-3',
      4: 'level-4', 5: 'level-5', 6: 'level-6'
    };
    return levels[Math.min(level, 6)] || levels[1];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!stats) return null;

  const { group, member_stats, progress_percentage } = stats;
  const isCreator = group.creator_id === user.id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="px-4 md:px-8 py-3 flex justify-between items-center max-w-7xl mx-auto">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Back</span>
          </Button>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-2 flex-wrap">
            {isCreator && (
              <>
                <Button
                  onClick={() => navigate(`/upi-setup/${groupId}`)}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid="upi-setup-btn"
                >
                  <Settings className="w-4 h-4 mr-2" /> {group.upi_id ? 'UPI' : 'Setup'}
                </Button>
                {pendingCount > 0 && (
                  <Button
                    onClick={() => navigate(`/verify/${groupId}`)}
                    size="sm"
                    className="rounded-full bg-orange-500 hover:bg-orange-600"
                    data-testid="verify-btn"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" /> Verify ({pendingCount})
                  </Button>
                )}
              </>
            )}
            <Button onClick={() => navigate(`/expenses/${groupId}`)} variant="outline" size="sm" className="rounded-full">
              <Receipt className="w-4 h-4 mr-2" /> Expenses
            </Button>
            <Button onClick={() => navigate(`/chat/${groupId}`)} variant="outline" size="sm" className="rounded-full">
              <MessageSquare className="w-4 h-4 mr-2" /> Chat
            </Button>
            <Button onClick={() => navigate(`/analytics/${groupId}`)} variant="outline" size="sm" className="rounded-full">
              <PieChart className="w-4 h-4 mr-2" /> Analytics
            </Button>
            <Button onClick={() => navigate(`/invite/${groupId}`)} variant="outline" size="sm" className="rounded-full" data-testid="invite-members-btn">
              <Share2 className="w-4 h-4 mr-2" /> Invite
            </Button>
            {isCreator && (
              <Button
                onClick={() => setShowDeleteDialog(true)}
                variant="outline"
                size="sm"
                className="rounded-full border-destructive text-destructive hover:bg-destructive/10"
                data-testid="delete-group-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Mobile Navigation Menu */}
          <div className="flex md:hidden items-center gap-2">
            {pendingCount > 0 && isCreator && (
              <Button
                onClick={() => navigate(`/verify/${groupId}`)}
                size="sm"
                className="rounded-full bg-orange-500 hover:bg-orange-600 px-2 h-8"
              >
                <AlertCircle className="w-4 h-4" />
                <span className="ml-1 text-xs">{pendingCount}</span>
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full px-2 h-8">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate(`/expenses/${groupId}`)}>
                  <Receipt className="w-4 h-4 mr-2" /> Expenses
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/chat/${groupId}`)}>
                  <MessageSquare className="w-4 h-4 mr-2" /> Chat
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/analytics/${groupId}`)}>
                  <PieChart className="w-4 h-4 mr-2" /> Analytics
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/invite/${groupId}`)}>
                  <Share2 className="w-4 h-4 mr-2" /> Invite Members
                </DropdownMenuItem>
                {isCreator && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate(`/upi-setup/${groupId}`)}>
                      <Settings className="w-4 h-4 mr-2" /> {group.upi_id ? 'UPI Settings' : 'Setup UPI'}
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setShowDeleteDialog(true)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete Group
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl gradient-primary p-8 text-white mb-8 relative overflow-hidden"
          data-testid="group-header"
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
          <div className="relative">
            <h1 className="text-3xl font-bold mb-2">{group.name}</h1>
            <p className="opacity-90 mb-6">{group.member_ids.length}/{group.num_members} members • {group.contribution_frequency}</p>
            
            <div className="mb-4">
              <div className="flex justify-between mb-2">
                <span className="opacity-90">Progress to Goal</span>
                <span className="font-semibold">{progress_percentage.toFixed(1)}%</span>
              </div>
              <div className="w-full h-3 bg-white/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(progress_percentage, 100)}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full bg-white rounded-full"
                />
              </div>
            </div>

            <div className="flex justify-between items-end">
              <div>
                <p className="text-sm opacity-90 mb-1">Current Amount</p>
                <p className="text-3xl font-bold font-mono">₹{group.current_amount.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <p className="text-sm opacity-90 mb-1">Goal Amount</p>
                <p className="text-2xl font-bold font-mono">₹{group.goal_amount.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* UPI Setup Alert */}
        {!group.upi_id && isCreator && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-2xl p-6 mb-6"
            data-testid="upi-setup-alert"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 dark:text-amber-100 mb-1">Payment Account Required</h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
                  Set up your UPI ID so members can send contributions
                </p>
                <Button
                  onClick={() => navigate(`/upi-setup/${groupId}`)}
                  size="sm"
                  className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Setup UPI ID Now
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:col-span-2 rounded-2xl bg-card border border-border p-6"
          >
            <Tabs defaultValue="contribute" className="w-full">
              <TabsList className="grid w-full grid-cols-2" data-testid="tabs-list">
                <TabsTrigger value="contribute" data-testid="contribute-tab">Contribute</TabsTrigger>
                <TabsTrigger value="ledger" data-testid="ledger-tab">Ledger</TabsTrigger>
              </TabsList>
              <TabsContent value="contribute" className="mt-6">
                <div className="space-y-4">
                  {group.upi_id ? (
                    <>
                      <div className="bg-muted/50 border border-border rounded-xl p-4">
                        <p className="text-sm text-muted-foreground mb-2">Send payment to:</p>
                        <p className="font-semibold mb-1">{group.bank_holder_name}</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 bg-background px-3 py-2 rounded-lg font-mono text-sm">
                            {group.upi_id}
                          </code>
                          <Button onClick={handleCopyUpi} variant="outline" size="sm" className="shrink-0">
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <Button
                          onClick={() => initiatePaymentWithDisclaimer(handlePayWithApp, 'gpay')}
                          variant="outline"
                          className="rounded-xl h-auto py-4"
                        >
                          <div className="text-center">
                            <p className="text-2xl font-bold">₹{group.daily_contribution}</p>
                            <p className="text-xs text-muted-foreground">Daily Amount</p>
                          </div>
                        </Button>
                        <Button
                          onClick={() => {
                            setCustomPayAmount('');
                            setShowCustomAmountDialog(true);
                          }}
                          variant="outline"
                          className="rounded-xl h-auto py-4"
                        >
                          <div className="text-center">
                            <Wallet className="w-6 h-6 mx-auto mb-1" />
                            <p className="text-xs text-muted-foreground">Custom Amount</p>
                          </div>
                        </Button>
                      </div>
                      
                      {/* Payment App Buttons */}
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground text-center">Pay with your preferred app:</p>
                        <div className="grid grid-cols-3 gap-2">
                          <Button
                            onClick={() => initiatePaymentWithDisclaimer(handlePayWithApp, 'gpay')}
                            variant="outline"
                            className="rounded-xl h-auto py-3 hover:bg-blue-50 hover:border-blue-300 dark:hover:bg-blue-950"
                            data-testid="pay-gpay-btn"
                          >
                            <div className="text-center">
                              <img 
                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Google_Pay_Logo.svg/512px-Google_Pay_Logo.svg.png" 
                                alt="Google Pay" 
                                className="w-8 h-8 mx-auto mb-1 object-contain"
                              />
                              <p className="text-xs">GPay</p>
                            </div>
                          </Button>
                          <Button
                            onClick={() => initiatePaymentWithDisclaimer(handlePayWithApp, 'phonepe')}
                            variant="outline"
                            className="rounded-xl h-auto py-3 hover:bg-purple-50 hover:border-purple-300 dark:hover:bg-purple-950"
                            data-testid="pay-phonepe-btn"
                          >
                            <div className="text-center">
                              <img 
                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/PhonePe_Logo.svg/512px-PhonePe_Logo.svg.png" 
                                alt="PhonePe" 
                                className="w-8 h-8 mx-auto mb-1 object-contain"
                              />
                              <p className="text-xs">PhonePe</p>
                            </div>
                          </Button>
                          <Button
                            onClick={() => initiatePaymentWithDisclaimer(handlePayWithApp, 'paytm')}
                            variant="outline"
                            className="rounded-xl h-auto py-3 hover:bg-sky-50 hover:border-sky-300 dark:hover:bg-sky-950"
                            data-testid="pay-paytm-btn"
                          >
                            <div className="text-center">
                              <img 
                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Paytm_Logo_%28standalone%29.svg/512px-Paytm_Logo_%28standalone%29.svg.png" 
                                alt="Paytm" 
                                className="w-8 h-8 mx-auto mb-1 object-contain"
                              />
                              <p className="text-xs">Paytm</p>
                            </div>
                          </Button>
                        </div>
                      </div>
                      
                      <Button
                        onClick={() => initiatePaymentWithDisclaimer(handleStartPayment)}
                        className="w-full rounded-full py-6 font-semibold gradient-primary"
                        data-testid="start-payment-btn"
                      >
                        <Wallet className="w-5 h-5 mr-2" /> Pay via Any UPI App
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">Payment account not configured yet</p>
                    </div>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="ledger" className="mt-6">
                <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="ledger-list">
                  {contributions.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No contributions yet</p>
                  ) : (
                    contributions.map((contribution, index) => {
                      const member = member_stats[contribution.user_id];
                      return (
                        <div
                          key={contribution.id}
                          className="flex justify-between items-center p-4 rounded-xl bg-muted/50 border border-border"
                          data-testid={`ledger-item-${index}`}
                        >
                          <div>
                            <p className="font-semibold">{member?.name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(contribution.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold gradient-text font-mono">₹{contribution.amount}</p>
                            <p className="text-xs text-green-600 dark:text-green-400">✓ Verified</p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </motion.div>

          {/* Sidebar - Members */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <div className="rounded-2xl bg-card border border-border p-6">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold" data-testid="members-section-title">Members</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(member_stats).map(([memberId, memberData], index) => (
                  <div
                    key={memberId}
                    className="flex justify-between items-center p-3 rounded-xl bg-muted/50"
                    data-testid={`member-card-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white font-bold">
                        {memberData.name[0]}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{memberData.name}</p>
                        <div className="flex items-center gap-2">
                          <span className={`level-badge ${getLevelBadge(memberData.level || 1)}`}>
                            <Zap className="w-3 h-3" /> Lv{memberData.level || 1}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-sm font-semibold gradient-text">₹{memberData.total_contributed}</p>
                      <p className="text-xs text-muted-foreground">{memberData.contribution_count} times</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="rounded-2xl bg-card border border-border p-6">
              <h3 className="font-semibold mb-4">Group Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Expenses</span>
                  <span className="font-semibold">₹{stats.total_expenses?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contributions</span>
                  <span className="font-semibold">{stats.total_contributions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Days Left</span>
                  <span className="font-semibold">
                    {Math.max(0, Math.ceil((new Date(group.end_date) - new Date()) / (1000 * 60 * 60 * 24)))}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Verify Your Payment</DialogTitle>
            <DialogDescription>
              After making the payment, enter the transaction details below
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitPayment} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="amount">Amount Paid (₹)</Label>
              <Input
                id="amount"
                type="number"
                placeholder="Enter amount"
                value={contributionAmount}
                onChange={(e) => setContributionAmount(e.target.value)}
                className="rounded-xl mt-2"
                data-testid="payment-amount-input"
              />
            </div>
            <div>
              <Label htmlFor="txnId">UPI Transaction ID</Label>
              <Input
                id="txnId"
                placeholder="e.g., 234567890123"
                value={upiTransactionId}
                onChange={(e) => setUpiTransactionId(e.target.value)}
                className="rounded-xl mt-2 font-mono"
                data-testid="transaction-id-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-full py-6 font-semibold gradient-primary"
              disabled={submitting}
              data-testid="submit-payment-btn"
            >
              {submitting ? 'Submitting...' : 'Submit for Verification'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Group?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{group.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteGroup}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {deleting ? 'Deleting...' : 'Delete Group'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Custom Amount Dialog */}
      <Dialog open={showCustomAmountDialog} onOpenChange={setShowCustomAmountDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Enter Custom Amount</DialogTitle>
            <DialogDescription>
              Enter the amount you want to pay
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="customAmount">Amount (₹)</Label>
              <Input
                id="customAmount"
                type="number"
                min="1"
                placeholder="Enter amount (min ₹1)"
                value={customPayAmount}
                onChange={(e) => setCustomPayAmount(e.target.value)}
                className="rounded-xl mt-2 text-lg"
                data-testid="custom-amount-input"
              />
            </div>
            <Button
              onClick={handlePayCustomAmount}
              className="w-full rounded-full py-6 font-semibold gradient-primary"
              data-testid="pay-custom-amount-btn"
            >
              Pay ₹{customPayAmount || '0'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Disclaimer Dialog */}
      <AlertDialog open={showPaymentDisclaimer} onOpenChange={setShowPaymentDisclaimer}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-500" />
              Payment Disclaimer
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left">
              <p>Before proceeding with your payment, please note:</p>
              <ul className="list-disc pl-5 space-y-2 text-sm">
                <li><strong>Direct Payments:</strong> All payments are made directly between you and the group admin/receiver via UPI. SharePay does not process, hold, or guarantee any transactions.</li>
                <li><strong>Your Responsibility:</strong> You are solely responsible for verifying the recipient's details and ensuring the payment is correct before confirming.</li>
                <li><strong>No Refunds:</strong> SharePay cannot reverse, refund, or mediate any payment disputes. Any issues must be resolved directly with your bank or the payment recipient.</li>
                <li><strong>Security:</strong> Always verify the UPI ID before making payments. Never share your UPI PIN with anyone.</li>
              </ul>
              <p className="text-amber-600 dark:text-amber-400 font-medium text-sm">
                By clicking "I Understand & Continue", you acknowledge that you have read and accept these terms.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={acceptDisclaimerAndPay}
              className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500"
              data-testid="accept-disclaimer-btn"
            >
              I Understand & Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GroupDetails;
